import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ScriptLoaderService } from 'src/app/services/script-loader.service';
import { shouldUseOzonetelSDK } from './ozonetel-dialer-mode';

declare var DigitalConsole: any;
declare var OneCxi: any;

export interface OzonetelConfig {
  customer: string;
  agentId: string;
  phoneNumber?: string;
  pin?: string;
  sip?: boolean;
  userName?: string;
  campaignName?: string;
}

export interface AgentState {
  status: string;
  mode: string;
  loggedIn: boolean;
  initialized: boolean;
}

export interface CallData {
  ucid?: string;
  did?: string;
  phoneNumber?: string;
  callType?: string;
  status?: string;
  startTime?: Date;
  endTime?: Date;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class OzonetelSDKService {
  private dc: any;
  private oneCXI: any;
  private isInitialized = false;
  private oneCxiInitialized = false;
  private isStreaming = false;
  private config: OzonetelConfig;
  private useSDK = shouldUseOzonetelSDK();
  private sdkLoaded = false;

  private agentStateSubject = new BehaviorSubject<AgentState>({
    status: 'UNKNOWN',
    mode: 'UNKNOWN',
    loggedIn: false,
    initialized: false
  });

  private callStateSubject = new Subject<CallData>();
  private callEventSubject = new Subject<any>();

  public agentState$ = this.agentStateSubject.asObservable();
  public callState$ = this.callStateSubject.asObservable();
  public callEvent$ = this.callEventSubject.asObservable();

  constructor(private http: HttpClient, private scriptLoader: ScriptLoaderService) {
    console.log('[OzonetelSDKService] Service initialized, useSDK:', this.useSDK);
  }

  async initializeAgent(config: OzonetelConfig): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        console.log('[OzonetelSDKService] Initializing agent with config:', {
          ...config,
          pin: '***'
        });

        this.config = config;

        if (!this.useSDK) {
          console.log('[OzonetelSDKService] SDK disabled - agent initialization skipped');
          console.log('[OzonetelSDKService] Set environment.ozonetel_sdk = true to enable SDK');
          this.isInitialized = false;
          this.agentStateSubject.next({
            status: 'SDK_DISABLED',
            mode: 'NONE',
            loggedIn: false,
            initialized: false
          });
          resolve({
            resultShort: 'skipped',
            resultLong: 'SDK disabled in environment configuration',
            sdkEnabled: false
          });
          return;
        }

        console.log('[OzonetelSDKService] SDK enabled, proceeding with initialization');

        if (!this.sdkLoaded) {
          await this.scriptLoader.loadOzonetelSDK();
          this.sdkLoaded = true;
          await new Promise(r => setTimeout(r, 500));
        }

        if (typeof DigitalConsole === 'undefined') {
          throw new Error('DigitalConsole SDK not loaded');
        }

        this.dc = new DigitalConsole();
        console.log('[OzonetelSDKService] DigitalConsole instance created');

        this.setupEventHandlers();

        const result = await this.dc.initialize(
          config.customer,
          config.agentId,
          config.phoneNumber || '',
          config.pin || '',
          config.sip !== false
        );

        console.log('[OzonetelSDKService] Initialize result:', result);

        if (result?.message?.includes('already logged in')) {
          console.log('[OzonetelSDKService] Agent already logged in, attempting logout and retry');
          await this.dc.logout(config.customer, config.agentId);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.initializeAgent(config);
        }

        const loginSuccess = result?.loggedIn === 'true' ||
          result?.loggedIn === true ||
          result?.success === true;

        if (!loginSuccess) {
          throw new Error('Login failed: ' + JSON.stringify(result));
        }

        this.isInitialized = true;
        this.agentStateSubject.next({
          status: 'IDLE',
          mode: 'UNKNOWN',
          loggedIn: true,
          initialized: true
        });

        console.log('[OzonetelSDKService] Agent initialized successfully');

        if (config.sip !== false) {
          await this.initializeOneCxi(config.userName);
        }

        resolve(result);
      } catch (error) {
        console.error('[OzonetelSDKService] Initialization error:', error);
        this.agentStateSubject.next({
          status: 'ERROR',
          mode: 'UNKNOWN',
          loggedIn: false,
          initialized: false
        });
        reject(error);
      }
    });
  }

  private async initializeOneCxi(userName: string): Promise<void> {
    if (!this.useSDK) {
      console.log('[OzonetelSDKService] SDK disabled, skipping OneCxi initialization');
      return;
    }

    if (!userName) {
      console.warn('[OzonetelSDKService] OneCxi userName missing, skipping initialization');
      return;
    }

    try {
      if (typeof OneCxi === 'undefined') {
        throw new Error('OneCxi SDK not loaded');
      }

      if (this.oneCxiInitialized) {
        console.log('[OzonetelSDKService] OneCxi already initialized');
        return;
      }

      console.log('[OzonetelSDKService] Fetching API key from backend...');
      const apiKeyResult: any = await firstValueFrom(
        this.http.get(`${environment.api_url}/api/ozonetel/config/api-key`)
      );

      if (!apiKeyResult || apiKeyResult.resultShort !== 'success' || !apiKeyResult.apiKey) {
        throw new Error('Failed to fetch API key from backend');
      }

      const apiKey = apiKeyResult.apiKey;

      console.log('[OzonetelSDKService] Initializing OneCxi for early SIP registration');
      this.oneCXI = new OneCxi();

      const oneCxiData = this.oneCXI.initializeSDK(userName, apiKey);
      console.log('[OzonetelSDKService] OneCxi initialization response:', oneCxiData);

      if (oneCxiData && oneCxiData.sip_url) {
        this.oneCxiInitialized = true;
        console.log('[OzonetelSDKService] OneCxi initialized successfully', {
          sipUrl: oneCxiData.sip_url,
          didNumbers: oneCxiData.did_numbers,
          extension: oneCxiData.extension
        });
        this.setupOneCxiEventHandlers();
      } else {
        console.warn('[OzonetelSDKService] OneCxi initialization returned no data or missing sip_url');
      }
    } catch (error) {
      console.error('[OzonetelSDKService] OneCxi initialization error:', error);
    }
  }

  async setAgentMode(mode: string, status: string = 'IDLE'): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Agent not initialized');
    }

    try {
      console.log('[OzonetelSDKService] Setting agent mode:', { mode, status });

      if (!this.useSDK) {
        const result: any = await firstValueFrom(this.http.post(`${environment.api_url}/api/ozonetel/agent/mode`, { mode, status }));
        this.agentStateSubject.next({
          ...this.agentStateSubject.value,
          mode,
          status
        });
        return result;
      }

      const result = await this.dc.setAgentMode(mode, status, false);
      console.log('[OzonetelSDKService] Set mode result:', result);

      this.agentStateSubject.next({
        ...this.agentStateSubject.value,
        mode,
        status
      });

      return result;
    } catch (error) {
      console.error('[OzonetelSDKService] Set mode error:', error);
      throw error;
    }
  }

  async getCampaigns(): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Agent not initialized');
    }

    try {
      console.log('[OzonetelSDKService] Fetching campaigns');

      if (!this.useSDK) {
        const result = await firstValueFrom(this.http.get(`${environment.api_url}/api/ozonetel/campaigns`));
        console.log('[OzonetelSDKService] Campaigns result (backend):', result);
        return result;
      }

      const result = await this.dc.getCampaigns();
      console.log('[OzonetelSDKService] Campaigns result:', result);
      return result;
    } catch (error) {
      console.error('[OzonetelSDKService] Get campaigns error:', error);
      throw error;
    }
  }

  async initManualCall(campId: string, phoneNumber: string, uui?: string): Promise<any> {
    if (!this.isInitialized) {
      throw new Error('Agent not initialized');
    }

    try {
      console.log('[OzonetelSDKService] Initiating manual call:', { campId, phoneNumber, uui });

      if (!this.useSDK) {
        const result: any = await firstValueFrom(this.http.post(`${environment.api_url}/api/ozonetel/call/manual`, {
          campId: campId,
          phoneNumber: phoneNumber,
          uui: uui
        }));
        console.log('[OzonetelSDKService] Manual call result (backend):', result);
        return result;
      }

      const result = await this.dc.initManualCall(campId, phoneNumber, false, uui || '');
      console.log('[OzonetelSDKService] Manual call result:', result);

      if (result && result.type === 'response') {
        console.log('[OzonetelSDKService] Minimal response received, will poll for call data');
        this.pollCallData();
      }

      return result;
    } catch (error) {
      console.error('[OzonetelSDKService] Manual call error:', error);
      throw error;
    }
  }

  private async pollCallData(): Promise<void> {
    const intervals = [2000, 5000, 10000];

    for (const interval of intervals) {
      await new Promise(resolve => setTimeout(resolve, interval));

      try {
        const callData = await this.dc.getCallData();
        console.log(`[OzonetelSDKService] Poll call data (${interval}ms):`, callData);

        if (callData && Object.keys(callData).length > 0 && callData.ucid) {
          console.log('[OzonetelSDKService] Call data found, triggering onNewCall manually');
          this.handleNewCall(callData);
          break;
        }
      } catch (error) {
        console.error('[OzonetelSDKService] Poll call data error:', error);
      }
    }
  }

  async logout(): Promise<any> {
    try {
      console.log('[OzonetelSDKService] Logging out agent');

      if (!this.useSDK) {
        const result = await firstValueFrom(this.http.post(`${environment.api_url}/api/ozonetel/agent/logout`, {}));
        this.isInitialized = false;
        this.agentStateSubject.next({
          status: 'LOGGED_OUT',
          mode: 'UNKNOWN',
          loggedIn: false,
          initialized: false
        });
        return result;
      }

      if (!this.dc) {
        throw new Error('DigitalConsole not initialized');
      }

      if (this.oneCXI && this.isStreaming) {
        this.oneCXI.stopStreaming();
        this.isStreaming = false;
      }

      const result = await this.dc.logout(this.config.customer, this.config.agentId);
      console.log('[OzonetelSDKService] Logout result:', result);

      this.isInitialized = false;
      this.oneCxiInitialized = false;
      this.agentStateSubject.next({
        status: 'LOGGED_OUT',
        mode: 'UNKNOWN',
        loggedIn: false,
        initialized: false
      });

      return result;
    } catch (error) {
      console.error('[OzonetelSDKService] Logout error:', error);
      throw error;
    }
  }

  private setupEventHandlers(): void {
    if (!this.dc) return;

    console.log('[OzonetelSDKService] Setting up DigitalConsole event handlers');

    this.dc.onNewCall = (data: any) => {
      console.log('[OzonetelSDKService] onNewCall event:', data);
      this.handleNewCall(data);
    };

    this.dc.onBusyAgent = (data: any) => {
      console.log('[OzonetelSDKService] onBusyAgent event:', data);
      this.agentStateSubject.next({
        ...this.agentStateSubject.value,
        status: 'BUSY'
      });
      this.callEventSubject.next({ type: 'BUSY', data });
    };

    this.dc.onDropCall = (data: any) => {
      console.log('[OzonetelSDKService] onDropCall event:', data);
      this.agentStateSubject.next({
        ...this.agentStateSubject.value,
        status: 'IDLE'
      });

      if (this.oneCXI && this.isStreaming) {
        console.log('[OzonetelSDKService] Stopping OneCxi stream on call drop');
        this.oneCXI.stopStreaming();
        this.isStreaming = false;
      }

      this.callEventSubject.next({ type: 'DROP', data });
    };

    this.dc.onCallDetails = (data: any) => {
      console.log('[OzonetelSDKService] onCallDetails event:', data);
      this.callEventSubject.next({ type: 'DETAILS', data });
    };

    this.dc.onAgentRelease = (data: any) => {
      console.log('[OzonetelSDKService] onAgentRelease event:', data);
      this.agentStateSubject.next({
        ...this.agentStateSubject.value,
        status: data.status || 'IDLE'
      });
      this.callEventSubject.next({ type: 'RELEASE', data });
    };

    this.dc.onDecline = (data: any) => {
      console.log('[OzonetelSDKService] onDecline event:', data);
      this.callEventSubject.next({ type: 'DECLINE', data });
    };

    this.dc.onPerformCallEvent = (data: any) => {
      console.log('[OzonetelSDKService] onPerformCallEvent:', data);
      this.callEventSubject.next({ type: 'PERFORM_CALL', data });
    };
  }

  private handleNewCall(data: any): void {
    const callData: CallData = {
      ucid: data.ucid,
      did: data.did,
      phoneNumber: data.phoneNumber || data.customerNumber,
      callType: data.callType,
      status: 'CONNECTED',
      startTime: new Date()
    };

    this.callStateSubject.next(callData);
    this.callEventSubject.next({ type: 'NEW_CALL', data });

    if (this.oneCXI && !this.isStreaming) {
      const didToUse = data.did || data.phoneNumber;
      if (didToUse) {
        console.log('[OzonetelSDKService] Starting OneCxi stream for call:', didToUse);
        try {
          this.oneCXI.startStreaming(didToUse);
          this.isStreaming = true;
          console.log('[OzonetelSDKService] OneCxi stream started successfully');
        } catch (error) {
          console.error('[OzonetelSDKService] Error starting OneCxi stream:', error);
        }
      }
    }
  }

  private setupOneCxiEventHandlers(): void {
    if (!this.oneCXI) return;

    console.log('[OzonetelSDKService] Setting up OneCxi event handlers');

    window.addEventListener('Started', (event: any) => {
      console.log('[OzonetelSDKService] OneCxi Started event:', event);
      this.callEventSubject.next({ type: 'STREAM_STARTED', data: event });
    });

    window.addEventListener('Ended', (event: any) => {
      console.log('[OzonetelSDKService] OneCxi Ended event:', event);
      this.isStreaming = false;
      this.callEventSubject.next({ type: 'STREAM_ENDED', data: event });
    });

    window.addEventListener('additionalText', (event: any) => {
      console.log('[OzonetelSDKService] OneCxi additionalText event:', event);
      this.callEventSubject.next({ type: 'ADDITIONAL_TEXT', data: event });
    });

    window.addEventListener('additionalTextUser', (event: any) => {
      console.log('[OzonetelSDKService] OneCxi additionalTextUser event:', event);
      this.callEventSubject.next({ type: 'USER_SPEAKING', data: event });
    });
  }

  isAgentInitialized(): boolean {
    return this.isInitialized;
  }

  isOneCxiReady(): boolean {
    return this.oneCxiInitialized;
  }

  getCurrentAgentState(): AgentState {
    return this.agentStateSubject.value;
  }
}
